// Cart Drawer JavaScript - Handles cart updates with bundled section rendering
class CartDrawer {
  constructor() {
    this.init();
  }

  init() {
    this.drawer = document.querySelector('[data-drawer-root="cart-drawer"]');
    this.refreshElements();

    // Delegate click events for cart actions
    document.addEventListener('click', this.handleClick.bind(this));

    // Listen for cart:add events from product forms
    document.addEventListener('cart:add', this.onCartAdd.bind(this));
  }

  refreshElements() {
    this.drawer = document.querySelector('[data-drawer-root="cart-drawer"]');
    if (!this.drawer) return;

    this.contentWrapper = this.drawer.querySelector('[data-cart-drawer-content]');
    this.loadingOverlay = this.drawer.querySelector('[data-cart-drawer-loading]');
    this.itemsContainer = this.drawer.querySelector('[data-cart-drawer-items]');
    this.deliveryPassContainer = this.drawer.querySelector('[data-cart-drawer-delivery-pass]');
    this.sectionId = this.contentWrapper?.dataset.sectionId;
  }

  handleClick(event) {
    // Handle cart quantity changes
    const cartChangeTarget = event.target.closest('[data-cart-change]');
    if (cartChangeTarget) {
      event.preventDefault();
      const line = cartChangeTarget.dataset.line;
      const quantity = parseInt(cartChangeTarget.dataset.quantity, 10);
      this.updateCart(line, quantity);
      return;
    }

    // Handle add to cart buttons
    const addToCartTarget = event.target.closest('[data-add-to-cart]');
    if (addToCartTarget) {
      event.preventDefault();
      const variantId = addToCartTarget.dataset.addToCart;
      this.addToCart(variantId);
      return;
    }
  }

  async addToCart(variantId, quantity = 1) {
    this.setLoading(true);
    this.clearLineItemErrors();

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          sections: this.getSectionIds(),
          sections_url: window.location.pathname
        })
      });

      const result = await response.json();
      if (!response.ok) {
        this.showLineItemError({
          variantId,
          message: this.getCartErrorMessage(result)
        });
        return;
      }

      // Fetch full cart for header count (add.js only returns the line item)
      const cartData = await this.fetchCart();
      this.updateHeaderCount(cartData.item_count);

      // Update sections from bundled response
      this.updateSections(result.sections);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showLineItemError({ variantId, message: 'Unable to add to cart.' });
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async onCartAdd(event) {
    // Refresh cart drawer when item is added
    await this.refreshSections();

    // Open the cart drawer
    if (window.Drawer) {
      window.Drawer.open('cart-drawer');
    }
  }

  async updateCart(line, quantity) {
    this.setLoading(true);
    this.clearLineItemErrors();

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: parseInt(line, 10),
          quantity: quantity,
          sections: this.getSectionIds(),
          sections_url: window.location.pathname
        })
      });

      const result = await response.json();
      if (!response.ok) {
        this.showLineItemError({
          line,
          message: this.getCartErrorMessage(result)
        });
        return;
      }

      // Update header with cart data from response
      this.updateHeaderCount(result.item_count);

      // Update sections from bundled response
      this.updateSections(result.sections);

    } catch (error) {
      console.error('Error updating cart:', error);
      this.showLineItemError({ line, message: 'Unable to update cart.' });
    } finally {
      this.setLoading(false);
    }
  }

  getSectionIds() {
    return [this.sectionId || 'cart-drawer'];
  }

  async refreshSections() {
    this.setLoading(true);

    try {
      // Get cart data for header count
      const cartData = await this.fetchCart();
      this.updateHeaderCount(cartData.item_count);

      // Get section HTML
      const sectionIds = this.getSectionIds();
      const params = new URLSearchParams();
      sectionIds.forEach(id => params.append('sections', id));

      const response = await fetch(`${window.location.pathname}?${params.toString()}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Section fetch failed');
      }

      const sections = await response.json();
      this.updateSections(sections);

    } catch (error) {
      console.error('Error refreshing sections:', error);
    } finally {
      this.setLoading(false);
    }
  }

  updateSections(sections) {
    if (!sections) return;

    const sectionId = this.sectionId || 'cart-drawer';
    const html = sections[sectionId] || sections['cart-drawer'];
    if (!html) return;

    this.updateCartDrawerSection(html);
  }

  updateCartDrawerSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Refresh our element references
    this.refreshElements();

    // Selectively update line items
    const newItems = doc.querySelector('[data-cart-drawer-items]');
    if (newItems && this.itemsContainer) {
      this.itemsContainer.innerHTML = newItems.innerHTML;
    }

    // Selectively update delivery pass (Liquid handles visibility via tag check)
    const newDeliveryPass = doc.querySelector('[data-cart-drawer-delivery-pass]');
    if (newDeliveryPass && this.deliveryPassContainer) {
      this.deliveryPassContainer.innerHTML = newDeliveryPass.innerHTML;
    }

    // Update header info (item count and subtotal)
    const newHeaderInfo = doc.querySelector('.cart-drawer__header-info');
    const currentHeaderInfo = this.drawer?.querySelector('.cart-drawer__header-info');
    if (newHeaderInfo && currentHeaderInfo) {
      currentHeaderInfo.innerHTML = newHeaderInfo.innerHTML;
    }

    // Update subtotal
    const newSubtotal = doc.querySelector('.cart-drawer__totals .money');
    const currentSubtotal = this.drawer?.querySelector('.cart-drawer__totals .money');
    if (newSubtotal && currentSubtotal) {
      currentSubtotal.innerHTML = newSubtotal.innerHTML;
    }

    // Update klarna message
    const newKlarnaMessage = doc.querySelector('.cart-klarna-placement');
    const currentKlarnaMessage = this.drawer?.querySelector('.cart-klarna-placement');
    if (newKlarnaMessage && currentKlarnaMessage) {
      currentKlarnaMessage.innerHTML = newKlarnaMessage.innerHTML;
    }

    // Update shipping CTAs (free delivery progress)
    const newCtas = doc.querySelector('.cart-drawer__ctas');
    const currentCtas = this.drawer?.querySelector('.cart-drawer__ctas');
    if (newCtas && currentCtas) {
      currentCtas.outerHTML = newCtas.outerHTML;
    }

    // Update free delivery message
    const newFreeDeliveryMsg = doc.querySelector('.cart-drawer__ctas + p');
    const currentFreeDeliveryMsg = this.drawer?.querySelector('.cart-drawer__ctas + p');
    // Handle cases where message appears/disappears
    const currentCtasEl = this.drawer?.querySelector('.cart-drawer__ctas');
    if (newFreeDeliveryMsg && currentFreeDeliveryMsg) {
      currentFreeDeliveryMsg.outerHTML = newFreeDeliveryMsg.outerHTML;
    } else if (newFreeDeliveryMsg && !currentFreeDeliveryMsg && currentCtasEl) {
      currentCtasEl.insertAdjacentHTML('afterend', newFreeDeliveryMsg.outerHTML);
    } else if (!newFreeDeliveryMsg && currentFreeDeliveryMsg) {
      currentFreeDeliveryMsg.remove();
    }

    // Handle empty cart state - if cart becomes empty, do a full content swap
    const newContent = doc.querySelector('[data-cart-drawer-content]');
    const isNowEmpty = !doc.querySelector('[data-cart-drawer-items]');
    const wasEmpty = !this.itemsContainer;

    if (isNowEmpty || wasEmpty) {
      // Cart state changed between empty/populated, do full swap
      if (newContent && this.contentWrapper) {
        this.contentWrapper.innerHTML = newContent.innerHTML;
        this.refreshElements();
      }
    }

    this.clearLineItemErrors();
  }

  getCartErrorMessage(errorResponse) {
    if (!errorResponse) return 'Unable to update cart.';

    if (typeof errorResponse === 'string') return errorResponse;

    if (Array.isArray(errorResponse.errors) && errorResponse.errors.length > 0) {
      return errorResponse.errors[0];
    }

    if (typeof errorResponse.errors === 'string') return errorResponse.errors;
    if (typeof errorResponse.description === 'string') return errorResponse.description;
    if (typeof errorResponse.message === 'string') return errorResponse.message;

    return 'Unable to update cart.';
  }

  clearLineItemErrors() {
    if (!this.drawer) return;

    const errors = this.drawer.querySelectorAll('[data-cart-item-error]');
    for (const errorEl of errors) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  }

  showLineItemError({ line, variantId, message }) {
    if (!this.drawer || !message) return;

    let item;
    if (line) {
      item = this.drawer.querySelector(`[data-cart-item][data-line="${line}"]`);
    } else if (variantId) {
      item = this.drawer.querySelector(`[data-cart-item][data-variant-id="${variantId}"]`);
    }

    const errorEl = item?.querySelector('[data-cart-item-error]');
    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.classList.remove('hidden');
  }

  updateHeaderCount(itemCount) {
    // Update cart count in header
    const cartButton = document.querySelector('[data-drawer-open="cart-drawer"]');
    if (!cartButton) return;

    let sup = cartButton.querySelector('sup');

    if (itemCount > 0) {
      if (sup) {
        sup.textContent = itemCount;
      } else {
        // Create new sup element
        sup = document.createElement('sup');
        sup.className = 'absolute top-0 right-0';
        sup.textContent = itemCount;
        cartButton.insertBefore(sup, cartButton.firstChild);
      }
    } else if (sup) {
      // Remove sup if cart is empty
      sup.remove();
    }
  }

  setLoading(loading) {
    // Refresh references in case section was updated
    this.refreshElements();

    if (!this.contentWrapper || !this.loadingOverlay) return;

    if (loading) {
      this.contentWrapper.classList.add('opacity-30', 'pointer-events-none');
      this.loadingOverlay.classList.remove('hidden');
    } else {
      this.contentWrapper.classList.remove('opacity-30', 'pointer-events-none');
      this.loadingOverlay.classList.add('hidden');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});

// Also initialize when sections are re-rendered (for Shopify preview)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    window.cartDrawer = new CartDrawer();
  });
}
